/**
 * Type guard for axios error objects
 */
export function isAxiosError(error: unknown): error is {
  response?: { data?: { error?: string } };
  message?: string;
  isAxiosError: boolean;
} {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error
  );
}

/**
 * Extended axios error type that includes validation fields
 */
interface AxiosErrorWithFields {
  response?: {
    data?: {
      error?: string;
      fields?: Record<string, string>;
      details?: Array<{ field: string; message: string }>;
    };
  };
  message?: string;
  isAxiosError: boolean;
}

/**
 * Type guard for axios error with fields
 */
function isAxiosErrorWithFields(error: unknown): error is AxiosErrorWithFields {
  return isAxiosError(error);
}

/**
 * Format field name for display (camelCase/snake_case to Title Case)
 */
function formatFieldName(field: string): string {
  if (!field) return 'Field';
  return field
    .replace(/([A-Z])/g, ' $1') // camelCase to spaces
    .replace(/_/g, ' ')         // snake_case to spaces
    .replace(/^\w/, c => c.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Safely extract error message from unknown error type.
 * Use this in catch blocks instead of accessing error.message directly.
 *
 * For validation errors, includes field-level details for better UX.
 */
export function getErrorMessage(error: unknown): string {
  // Handle axios errors with response data
  if (isAxiosErrorWithFields(error)) {
    const data = error.response?.data;

    // If there are validation field errors, format them nicely
    if (data?.fields && Object.keys(data.fields).length > 0) {
      const fieldErrors = Object.entries(data.fields)
        .map(([field, message]) => `\u2022 ${formatFieldName(field)}: ${message}`)
        .join('\n');
      return `${data.error || 'Validation failed'}:\n${fieldErrors}`;
    }

    // Check details array (alternative format)
    if (data?.details && data.details.length > 0) {
      const fieldErrors = data.details
        .map(d => `\u2022 ${formatFieldName(d.field)}: ${d.message}`)
        .join('\n');
      return `${data.error || 'Validation failed'}:\n${fieldErrors}`;
    }

    return data?.error || error.message || 'An error occurred';
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred';
}

/**
 * Generate user-friendly error messages for common API error patterns.
 * Use this in page components to provide consistent, helpful error messages.
 *
 * @param error - The error from an API call (typically from catch block)
 * @param featureName - A human-readable name for the feature (e.g., "rewards", "contracts")
 * @returns A user-friendly error message string
 */
export function getApiErrorMessage(error: unknown, featureName: string = 'data'): string {
  const err = error as { response?: { status?: number; data?: { error?: string } } } | undefined;
  // Handle specific HTTP status codes
  if (err?.response?.status === 404) {
    return `${capitalize(featureName)} feature is not available yet.`;
  }
  if (err?.response?.status === 401) {
    return `Please log in to view ${featureName}.`;
  }
  if (err?.response?.status === 403) {
    return `You don't have permission to view ${featureName}.`;
  }
  if (err?.response?.status && err.response.status >= 500) {
    return `Server error. Please try again later.`;
  }

  // Handle network issues
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'No internet connection. Please check your network.';
  }

  // Check for custom error message from backend
  if (err?.response?.data?.error) {
    return err.response.data.error;
  }

  // Default fallback
  return `Unable to load ${featureName}. Please try again.`;
}

/**
 * Safely extract data from API response with fallback.
 * Handles responses wrapped in { data: ... } or { items: ... } patterns.
 *
 * @param response - The axios response object
 * @param key - The key to extract from response.data (e.g., 'rewards', 'items')
 * @param fallback - Default value if extraction fails
 */
export function extractApiData<T>(
  response: { data?: Record<string, unknown> },
  key: string,
  fallback: T
): T {
  if (!response?.data) return fallback;

  // If the data is directly the value we want (no wrapper)
  if (Array.isArray(response.data) && Array.isArray(fallback)) {
    return response.data as T;
  }

  // If wrapped in { key: value } structure
  if (response.data[key] !== undefined) {
    return response.data[key] as T;
  }

  // Return fallback
  return fallback;
}

// Helper function for capitalizing first letter
function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Codes returned by requireWriteAccess/requireBiometricVerification
// (backend/src/middleware/ageVerificationMiddleware.ts) when a DOB-only user's grace
// period has ended and a write is blocked pending biometric verification.
// AGE_VERIFICATION_REQUIRED (no age verification at all) is deliberately excluded — its
// backend verification_url is /age-verification, not /settings/profile, and
// VerificationUpgradePrompt always navigates to /settings/profile.
const VERIFICATION_BLOCK_CODES = ['GRACE_PERIOD_READ_ONLY', 'BIOMETRIC_VERIFICATION_REQUIRED'];
const VERIFICATION_BLOCK_FALLBACK_MESSAGE = 'Complete biometric verification to continue.';

/**
 * Returns the message to show via <VerificationUpgradePrompt> if this error is a
 * DOB-grace-period write block, else null (caller should fall back to a normal error).
 */
export function getVerificationBlockMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
  if (data?.code && VERIFICATION_BLOCK_CODES.includes(data.code)) {
    return data.message || VERIFICATION_BLOCK_FALLBACK_MESSAGE;
  }
  return null;
}
